// react
import { useContext, useMemo, useState } from 'react';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// types
import { Entity, Frame, Pagination, Transaction } from '../../../../@types';
import { transactionStatuses } from '../../../../@types/unions';

// router
import { useLocation, useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { BtnLegendTransaction } from '../../../ui/BtnLegendTransaction/BtnLegendTransaction';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardNonCashBenefit } from '../../../cards/CardNonCashBenefit/CardNonCashBenefit';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// utils
import { translateStatus } from '../../../../utils/translations';

// props
export interface TableNonCashBenefitsProps {
	frame?: Frame;
	hasLegend?: boolean;
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions?: () => void;
	transactions: Transaction[];
}

export const TableNonCashBenefits = ({
	frame = 'PROJECT',
	hasLegend = true,
	hasSearch = true,
	hasSelectPage = true,
	isLoading,
	pagination,
	refetchTransactions,
	transactions,
}: TableNonCashBenefitsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramStatus = searchParams.get('status');
	const paramTags = searchParams.get('tags');
	const paramToEntity = searchParams.get('toEntity');

	// defaults
	const defaultFiltersValues = {
		loan: 'all',
		status: 'all',
		tags: [],
		toEntity: 'all',
	};

	// state
	const [beneficiaries, setBeneficiaries] = useState<Entity[]>([]);
	const [filtersValues, setFiltersValues] = useState({
		status: paramStatus || defaultFiltersValues.status,
		tags: paramTags ? paramTags.split(',') : defaultFiltersValues.tags,
		toEntity: paramToEntity || defaultFiltersValues.toEntity,
	});

	// fetch beneficiaries
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?fields=id,name&project=${project?.id}&type=BENEFICIARY`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiaries(res.data);
			}
		},
	});

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 2,
			},
			isSortable: true,
			label: t('date'),
			value: 'date',
		},
	];

	if (frame !== 'ENTITY') {
		headers.push({
			GridProps: {
				lg: frame === 'PROJECT' ? 2 : 3,
			},
			label: t('entity'),
			value: 'fromEntity',
		});
	}
	if (frame !== 'BENEFICIARY') {
		headers.push({
			GridProps: {
				lg: frame === 'PROJECT' ? 2 : 3,
			},
			label: t('beneficiary'),
			value: 'toEntity',
		});
	}

	headers.push(
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: {
				lg: frame === 'BENEFICIARY' ? 2 : 1.5,
			},
			label: t('benefit'),
			value: 'benefit',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('updated'),
			value: 'modified',
		}
	);

	const idFormFilters = 'form-nonCashBenefits-filters';

	// options
	const optionsBeneficiaries = beneficiaries.map((beneficiary) => {
		return {
			label: beneficiary.name,
			value: String(beneficiary.id),
		};
	});

	const optionsStatus = transactionStatuses.map((status) => {
		return {
			label: translateStatus(status, t),
			value: status,
		};
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [];

	if (frame === 'PROJECT') {
		if (userProjectPermissions?.beneficiary?.read) {
			filters.push({
				hasAllOption: true,
				hasNoneOption: false,
				hasSearchParam: true,
				id: `${idFormFilters}-beneficiary`,
				label: t('beneficiary'),
				name: 'toEntity',
				onChange: handleChangeFilterValue,
				options: optionsBeneficiaries,
				type: 'SELECT',
				value: filtersValues.toEntity,
			});
		}
	}

	filters.push({
		hasAllOption: true,
		hasNoneOption: false,
		hasSearchParam: true,
		id: `${idFormFilters}-status`,
		label: t('status'),
		name: 'status',
		onChange: handleChangeFilterValue,
		options: optionsStatus,
		type: 'SELECT',
		value: filtersValues.status,
	});

	if (userProjectPermissions?.tag?.read) {
		filters.push({
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: handleChangeFilterValue,
			type: 'TAGS',
			value: filtersValues.tags,
		});
	}

	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: {
				children: t('empty.nonCashBenefits.content'),
			},
			IconCircleProps: {
				color: 'grey',
				type: 'nonCashBenefit',
			},
			title: { children: t('empty.nonCashBenefits.title') },
		};

		if (
			emptyProps.actions &&
			userProjectPermissions?.nonCashBenefit.create &&
			!location.pathname.includes('entity')
		) {
			emptyProps.actions.push({
				children: t('recordNonCashBenefit'),
				onClick: () => {
					searchParams.set('dialog', 'recordNonCashBenefit');
					setSearchParams(searchParams);
				},
				variant: 'contained',
			});
		}

		// redefine empty content if search params exist
		if ([...searchParams].length && !paramDialog) {
			emptyProps.content = {
				children: t('empty.default.content'),
			};
			emptyProps.IconCircleProps = {
				icon: null,
			};
		}

		return emptyProps;
	}, [location, paramDialog, searchParams, setSearchParams, t, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction, i) => {
					return (
						<Grid item key={`transaction-non-cash-benefit-${i}`} xs={12}>
							<CardNonCashBenefit
								frame={frame}
								refetchTransactions={refetchTransactions}
								transaction={transaction}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{hasSelectPage && (
						<Grid item xs={12} md={3} lg={2}>
							<SelectTransactionPage value="nonCashBenefits" />
						</Grid>
					)}
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrEntity') }} />
						</Grid>
					)}
					{hasLegend && (
						<Grid item xs={4} md="auto">
							<BtnLegendTransaction />
						</Grid>
					)}
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnExport resource="NON_CASH_BENEFIT" />
					</Grid>
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
